import { useCallback, useEffect, useState } from "react";
import {
  propiedadesApi,
  Bodega,
  GeoCountry,
  GeoRegion,
  GeoComuna,
  Sector,
} from "../../services/propiedades.api";

export type BodegaFormValues = Omit<Bodega, "id">;

export function useBodegasLogic() {
  const [bodegas, setBodegas] = useState<Bodega[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Bodega | null>(null);

  const [paisOptions, setPaisOptions] = useState<GeoCountry[]>([]);
  const [regionOptions, setRegionOptions] = useState<GeoRegion[]>([]);
  const [comunaOptions, setComunaOptions] = useState<GeoComuna[]>([]);
  const [sectorOptions, setSectorOptions] = useState<Sector[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);

  const fetchBodegas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await propiedadesApi.getBodegas();
      setBodegas(Array.isArray(res?.data) ? res!.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCatalogs = useCallback(async () => {
    setCatalogsLoading(true);
    try {
      const [pRes, rRes, cRes, sRes] = await Promise.all([
        propiedadesApi.getCountries(),
        propiedadesApi.getRegions(),
        propiedadesApi.getComunas(),
        propiedadesApi.getSectores(),
      ]);
      setPaisOptions(Array.isArray(pRes?.data) ? pRes!.data : []);
      setRegionOptions(Array.isArray(rRes?.data) ? rRes!.data : []);
      setComunaOptions(Array.isArray(cRes?.data) ? cRes!.data : []);
      setSectorOptions(Array.isArray(sRes?.data) ? sRes!.data : []);
    } finally {
      setCatalogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBodegas();
    fetchCatalogs();
  }, [fetchBodegas, fetchCatalogs]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((bodega: Bodega) => {
    setEditing(bodega);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const createBodega = useCallback(
    async (values: BodegaFormValues) => {
      const res = await propiedadesApi.createBodega(values);
      if (res?.success) {
        await fetchBodegas();
        closeModal();
      }
      return !!res?.success;
    },
    [fetchBodegas, closeModal],
  );

  const updateBodega = useCallback(
    async (id: number, values: Partial<BodegaFormValues>) => {
      const res = await propiedadesApi.updateBodega(id, values);
      if (res?.success) {
        await fetchBodegas();
        closeModal();
      }
      return !!res?.success;
    },
    [fetchBodegas, closeModal],
  );

  const deleteBodega = useCallback(
    async (id: number) => {
      await propiedadesApi.deleteBodega(id);
      await fetchBodegas();
    },
    [fetchBodegas],
  );

  return {
    bodegas,
    loading,
    modalOpen,
    editing,
    catalogsLoading,
    paisOptions,
    regionOptions,
    comunaOptions,
    sectorOptions,
    openCreate,
    openEdit,
    closeModal,
    createBodega,
    updateBodega,
    deleteBodega,
    fetchBodegas,
  };
}
